import { HttpInterceptorFn, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Auth interceptor that attaches the Cognito ID token as a Bearer token
 * to outgoing API requests and handles 401 responses.
 *
 * Only attaches to requests going to our API backend — NOT to Cognito token/userInfo endpoints.
 */
export const authInterceptor: HttpInterceptorFn = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
) => {
    const router = inject(Router);
    const cognitoDomain = environment.cognito.domain;

    // Don't attach our token to Cognito's own endpoints
    if (req.url.includes(cognitoDomain)) {
        return next(req);
    }

    // Only attach token to our API requests
    let authReq = req;
    if (req.url.startsWith(environment.apiBaseUrl)) {
        const session = getStoredSession();
        if (session?.idToken) {
            authReq = req.clone({
                setHeaders: {
                    Authorization: `Bearer ${session.idToken}`,
                },
            });
        }
    }

    return next(authReq).pipe(
        catchError((error) => {
            if (error.status === 401) {
                localStorage.removeItem('wg_session');
                router.navigate(['/login']);
            }
            return throwError(() => error);
        }),
    );
};

function getStoredSession(): { idToken: string; accessToken: string } | null {
    try {
        const raw = localStorage.getItem('wg_session');
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}
